import React, { useState } from 'react';
import { LoanOfficerMetrics, FunnelStep } from '../../types/throughput';
import { Card, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
interface LoanOfficerTableProps {
  metrics: LoanOfficerMetrics[];
  onRowClick: (officerId: string) => void;
}
type SortField = 'name' | 'Created' | 'Converted' | 'Funded' | 'conversion';
type SortDirection = 'asc' | 'desc';
export function LoanOfficerTable({
  metrics,
  onRowClick
}: LoanOfficerTableProps) {
  const [sortField, setSortField] = useState<SortField>('Funded');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [search, setSearch] = useState('');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const sortedMetrics = [...metrics].filter(m => m.officer.name.toLowerCase().includes(search.toLowerCase())).sort((a, b) => {
    let valA: any;
    let valB: any;
    if (sortField === 'name') {
      valA = a.officer.name;
      valB = b.officer.name;
    } else if (sortField === 'conversion') {
      valA = a.conversion.createdToFunded;
      valB = b.conversion.createdToFunded;
    } else {
      valA = a.counts[sortField as FunnelStep];
      valB = b.counts[sortField as FunnelStep];
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <span className="w-4 h-4 inline-block" data-id="element-203" />;
    return sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5" data-id="element-204" /> : <ChevronDown className="w-3.5 h-3.5" data-id="element-205" />;
  };
  const SortableHeader = ({
    field,
    children,
    className = ''
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => <th className={`px-4 py-3 font-semibold cursor-pointer hover:bg-[#EBEBEB] transition-colors select-none ${className}`} onClick={() => handleSort(field)} data-id="element-206">
      <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`} data-id="element-207">
        {children}
        <SortIcon field={field} data-id="element-208" />
      </div>
    </th>;
  return <Card className="p-0 overflow-hidden" noPadding data-id="element-209">
      <CardHeader bordered className="!mb-0" data-id="element-210">
        <CardTitle data-id="element-211">Performance by Loan Officer</CardTitle>
        <div className="relative" data-id="element-212">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#706E6B]" data-id="element-213" />
          <input type="text" placeholder="Search officers..." value={search} onChange={e => setSearch(e.target.value)} className="w-56 h-8 pl-9 pr-3 text-sm bg-white border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0176D3]/20 focus:border-[#0176D3] transition-colors" data-id="element-214" />
        </div>
      </CardHeader>

      <div className="overflow-x-auto" data-id="element-215">
        <table className="w-full text-sm" data-id="element-216">
          <thead className="text-xs text-[#706E6B] uppercase bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-217">
            <tr data-id="element-218">
              <SortableHeader field="name" data-id="element-219">Officer</SortableHeader>
              <th className="px-4 py-3 font-semibold" data-id="element-220">Status</th>
              <SortableHeader field="Created" className="text-right" data-id="element-221">
                Created
              </SortableHeader>
              <SortableHeader field="Converted" className="text-right" data-id="element-222">
                Converted
              </SortableHeader>
              <th className="px-4 py-3 font-semibold text-right hidden lg:table-cell" data-id="element-223">
                Disc. Sent
              </th>
              <th className="px-4 py-3 font-semibold text-right hidden lg:table-cell" data-id="element-224">
                Sub. UW
              </th>
              <th className="px-4 py-3 font-semibold text-right hidden xl:table-cell" data-id="element-225">
                AWC
              </th>
              <SortableHeader field="Funded" className="text-right text-[#04844B]" data-id="element-226">
                Funded
              </SortableHeader>
              <SortableHeader field="conversion" className="text-right" data-id="element-227">
                Pull-through
              </SortableHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]" data-id="element-228">
            {sortedMetrics.length === 0 ? <tr data-id="element-229">
                <td colSpan={9} className="px-4 py-12 text-center text-[#706E6B]" data-id="element-230">
                  {search ? 'No officers match your search.' : 'No data available.'}
                </td>
              </tr> : sortedMetrics.map(row => <tr key={row.officer.id} className="bg-white hover:bg-[#F5F9FD] transition-colors cursor-pointer group" onClick={() => onRowClick(row.officer.id)} data-id="element-231">
                  <td className="px-4 py-3" data-id="element-232">
                    <span className="font-medium text-[#0176D3] group-hover:underline" data-id="element-233">
                      {row.officer.name}
                    </span>
                  </td>
                  <td className="px-4 py-3" data-id="element-234">
                    {row.officer.isActive ? <Badge variant="success" data-id="element-235">Active</Badge> : <Badge variant="neutral" data-id="element-236">Inactive</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#181818]" data-id="element-237">
                    {row.counts['Created']}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#181818]" data-id="element-238">
                    {row.counts['Converted']}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#706E6B] hidden lg:table-cell" data-id="element-239">
                    {row.counts['Disclosures Sent']}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#706E6B] hidden lg:table-cell" data-id="element-240">
                    {row.counts['Submitted to UW']}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#706E6B] hidden xl:table-cell" data-id="element-241">
                    {row.counts['Approved with Conditions']}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#04844B]" data-id="element-242">
                    {row.counts['Funded']}
                  </td>
                  <td className="px-4 py-3" data-id="element-243">
                    <div className="flex items-center justify-end gap-2" data-id="element-244">
                      <div className="w-14 h-1.5 bg-[#E5E5E5] rounded-full overflow-hidden" data-id="element-245">
                        <div className="h-full bg-[#04844B] rounded-full transition-all duration-300" style={{
                    width: `${Math.min(row.conversion.createdToFunded, 100)}%`
                  }} data-id="element-246" />
                      </div>
                      <span className="text-xs font-medium tabular-nums w-9 text-right text-[#181818]" data-id="element-247">
                        {row.conversion.createdToFunded.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>

      {sortedMetrics.length > 0 && <div className="px-4 py-3 border-t border-[#E5E5E5] bg-[#FAFAFA] text-xs text-[#706E6B]" data-id="element-248">
          Showing {sortedMetrics.length} of {metrics.length} officers
        </div>}
    </Card>;
}